/**
 * Resolves the capture time zone evidence for a HealthKit observation upload.
 *
 * The dashboard API only derives canonical fertility signals from HealthKit
 * observations when the upload carries `observedTimeZone` (IANA identifier)
 * and `observedUtcOffset` (ISO offset such as "-08:00"), and it verifies that
 * the offset — and `localDate`, when present — actually match that zone at the
 * observation timestamp. Everything here is therefore computed from one zone
 * resolution so the three fields can never disagree with each other.
 *
 * HealthKit stamps samples with the zone they were recorded in via the
 * `HKTimeZone` metadata key. When a sample carries it, that zone wins over the
 * device's current zone: a sample recorded in Paris stays a Paris observation
 * even when the sync happens in New York.
 */

/** Apple's HKMetadataKeyTimeZone constant as it appears in sample metadata. */
const HEALTHKIT_TIME_ZONE_METADATA_KEY = "HKTimeZone";

export type ObservedCaptureZone = {
  observedTimeZone: string | null;
  observedUtcOffset: string | null;
  localDate: string;
};

function isUsableTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(0);
    return true;
  } catch {
    return false;
  }
}

function metadataTimeZone(metadata: unknown): string | null {
  if (typeof metadata !== "object" || metadata === null) return null;
  const value = (metadata as Record<string, unknown>)[
    HEALTHKIT_TIME_ZONE_METADATA_KEY
  ];
  return typeof value === "string" && value.length > 0 ? value : null;
}

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const resolved: ZonedParts = {
    year: 0,
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
  };
  for (const part of parts) {
    if (part.type in resolved) {
      resolved[part.type as keyof ZonedParts] = parseInt(part.value, 10);
    }
  }
  return resolved;
}

/**
 * Compute the ISO UTC offset ("+02:00") of a zone at a specific instant.
 *
 * Derived by re-reading the instant's wall-clock components in the target
 * zone and diffing against the UTC timestamp, so daylight saving is resolved
 * for the observation's own moment rather than for "now". This mirrors the
 * Temporal-based check the dashboard API runs before deriving a fertility
 * signal; a mismatch there silently drops the signal.
 */
export function utcOffsetAt(date: Date, timeZone: string): string {
  const parts = zonedParts(date, timeZone);
  const wallClockAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  const truncatedInstant = Math.floor(date.getTime() / 1000) * 1000;
  const offsetMinutes = Math.round(
    (wallClockAsUtc - truncatedInstant) / 60_000,
  );
  const sign = offsetMinutes < 0 ? "-" : "+";
  const absolute = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, "0");
  const minutes = String(absolute % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
}

function localDateInZone(date: Date, timeZone: string): string {
  const parts = zonedParts(date, timeZone);
  return [
    String(parts.year).padStart(4, "0"),
    String(parts.month).padStart(2, "0"),
    String(parts.day).padStart(2, "0"),
  ].join("-");
}

function deviceLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Resolve the zone evidence for one sample.
 *
 * Zone precedence: the sample's own `HKTimeZone` metadata, then the caller's
 * fallback (the device zone at sync time). When neither resolves to a zone
 * Intl can evaluate, both observed fields stay null — the raw observation
 * still uploads, matching today's behavior — and `localDate` falls back to
 * the device-local calendar date.
 */
export function resolveObservedCaptureZone({
  startDate,
  metadata,
  fallbackTimeZone,
}: {
  startDate: Date;
  metadata: unknown;
  fallbackTimeZone: string | null;
}): ObservedCaptureZone {
  const candidates = [metadataTimeZone(metadata), fallbackTimeZone];
  for (const candidate of candidates) {
    if (!candidate || !isUsableTimeZone(candidate)) continue;
    return {
      observedTimeZone: candidate,
      observedUtcOffset: utcOffsetAt(startDate, candidate),
      localDate: localDateInZone(startDate, candidate),
    };
  }
  return {
    observedTimeZone: null,
    observedUtcOffset: null,
    localDate: deviceLocalDate(startDate),
  };
}
